import * as React from 'react';

import {styled, emotionStyled} from '@packages/utilities/styled';
import {Copy} from '../Text';
import type {CSSProperties} from '@packages/utilities/css-properties-type';
import useResizeObserver from '@packages/systems/ui/hooks/useResizeObserver';

const {useState} = React;

const TabButton = styled(Copy)(({theme, hover, isCurrent, disabled, style}) => {
  const {typography, gutters, Tab} = theme;
  let color = Tab.textColor;
  let opacity = 1;

  if (disabled) {
    opacity = 0.6;
  } else if (hover || isCurrent) {
    color = Tab.textColorHover;
  }

  return {
    fontWeight: isCurrent ? typography.subheading.fontWeight : undefined,
    fontSize: typography.subheading.fontSize,
    lineHeight: `${typography.subheading.lineHeight}px`,
    position: 'relative',
    color,
    pointerEvents: disabled && 'none',
    cursor: 'default',
    userSelect: 'none',
    maxWidth: '100%',
    opacity,
    paddingTop: gutters.x12,
    paddingBottom: gutters.x12,
    ...style,
  };
});

const TabIndicatorView = emotionStyled('div')(
  {
    position: 'absolute',
    width: '100%',
    height: 1.5,
    bottom: 0,
    backgroundColor: 'transparent',
  },
  {
    variants: {
      isActive: {
        true: {
          backgroundColor: '$textPrimary',
        },
      },
    },
  }
);

type TabProps = {
  disabled?: boolean;
  children: React.ReactNode;
  style?: CSSProperties;
  isCurrent?: boolean;
  onClick?: () => void;
  id?: string;
  ['data-automation-id']?: string;
};
/**
 * `<Tab />`
 *
 * _Renders a Tab. Accepts an onClick handler, disabled flag, and isCurrent flag._
 *
 * [Storybook 📚](http://localhost:9001/?selectedKind=Tab&selectedStory=Defaults)
 *
 * @deprecated use `Tabs`, `TabList`, `Tab`, and `TabPanel` from `@packages/systems/spring` instead
 **/
const Tab = ({
  disabled = false,
  children,
  style,
  isCurrent = false,
  onClick,
  id,
  'data-automation-id': dataAutomationId,
}: TabProps) => {
  const [highlighted, setHighlight] = useState(false);
  const [fixedWidth, setFixedWidth] = useState<null | number>(null);

  // Set a fixed width to prevent a layout shifting bug when active tab
  // changes. The layout shift bug occurs when text is bolded.
  const fixedWidthRef = React.useRef<
    | undefined
    | {
        width: number;
      }
  >(undefined);
  const resizeObserver = useResizeObserver(
    // @ts-expect-error - TS2345 - Argument of type '([{ contentRect: { width }, },]: [any]) => void' is not assignable to parameter of type '(entries: ResizeObserverEntry[]) => void'.
    React.useCallback(
      ([
        {
          contentRect: {width},
        },
      ]: [any]) => {
        if (fixedWidthRef.current == null) {
          setFixedWidth(Math.ceil(width));
        }
      },
      []
    )
  );

  return (
    <TabButton
      disabled={disabled}
      style={style}
      onMouseOver={() => setHighlight(true)}
      onMouseOut={() => setHighlight(false)}
      onClick={onClick}
      hover={highlighted}
      isCurrent={!disabled && isCurrent}
      id={id}
      data-legacy-ds="Tab"
    >
      <div
        // @ts-expect-error - TS2322 - Type 'number | null' is not assignable to type 'Width<string | number> | undefined'.
        style={{width: fixedWidth}}
        ref={(el) => {
          el && resizeObserver.observe(el);
        }}
        data-automation-id={dataAutomationId}
      >
        {children}
      </div>
      <TabIndicatorView
        isActive={isCurrent && !disabled}
        data-automation-id="tab-button-active-indicator"
      />
    </TabButton>
  );
};

export default React.memo(Tab) as React.ComponentType<TabProps>;
